﻿// Strips Zepto's preview property off cart lines that carry no engraving.
//
// Zepto attaches its rendered preview as a PUBLIC line item property (`Preview`, or
// `zepto_preview_image_label`, valued with a `…/uploads/<hash>` URL) to EVERY line it
// manages — also to lines with nothing to look at: spec-only products (HOLD/BAND, whose
// Zepto fields are pure measurements) and lines where the customer left the engraving
// blank. Being public, Shopify then renders it as a "Preview" link in checkout, on the
// thank-you page, in the order confirmation email and on the admin order page. None of
// those run theme code, so cart-line-properties.liquid cannot reach them — the only lever
// is to remove the property from the cart line before it becomes an order.
//
// The gate deciding "does this line have an engraving" is a JS mirror of the Liquid gate
// in cart-line-properties.liquid, fed from the same blocklist snippet
// (zepto-field-registry.liquid 's' rows → window.byRavnPreviewStrip.specOnlyLabels). Keep the two
// algorithms in step; the blocklist itself cannot drift.
//
// Fails safe everywhere: anything unexpected — missing config, a failed /cart.js, a
// properties bag that is not an object — classifies the line as personalized, so it is
// left alone and the Liquid gate + CSS keep hiding the link on the theme's own surfaces
// exactly as they do today.
(function () {
  // 'purge' deletes the property outright — the only mode that also clears the Shopify
  // ADMIN order page. 'privatize' is the escape hatch: it keeps the URL in a private
  // property, invisible to the customer but still shown to the merchant in admin.
  const MODE = 'purge';
  const PRIVATE_KEY = '_byRavn_preview';
  // /cart/change.js cannot set the properties object to {} once properties exist, and an
  // unpersonalized line can consist of nothing but the preview. A private sentinel keeps
  // the payload valid and is invisible on every surface.
  const PURGE_SENTINEL = '_byRavn_no_preview';

  const PREVIEW_NAMES = ['preview', 'zepto_preview_image_label'];
  const PUBLISH_SOURCE = 'byRavn-preview-strip';
  // Unit separator: cannot occur in a property name or value, so joining the identity
  // pairs on it stays unambiguous.
  const UNIT = String.fromCharCode(31);

  const SWEEP_DEBOUNCE_MS = 400;
  const MIN_SWEEP_INTERVAL_MS = 1500;
  // Strictly after byRavn-cart-drawer-open.js's 250 ms drawer debounce, so the drawer has
  // already painted by the time we re-key anything.
  const ZEPTO_ADD_DELAY_MS = 700;
  const ZEPTO_ADD_RETRY_MS = 1500;
  const MAX_ATTEMPTS = 2;
  const MAX_LINES_PER_SWEEP = 10;
  const STORE_KEY = 'byRavn:previewStrip:attempts';
  const STORE_LIMIT = 50;

  let busy = false;
  let disabled = false;
  let lastSweepAt = 0;
  let trailingTimer = null;
  let debounceTimer = null;

  // --- gate: JS mirror of cart-line-properties.liquid --------------------------------

  // Liquid slices off one `_`, then a second if present: with Zepto's per-product
  // hide-in-checkout option on, Zepto prefixes another `_` to every property, so the same
  // field is `_Diameter` on one product and `__Diameter` on another.
  function normalizeKey(rawKey) {
    let key = rawKey == null ? '' : String(rawKey);
    if (key.charAt(0) === '_') {
      key = key.slice(1);
      if (key.charAt(0) === '_') key = key.slice(1);
    }
    return key;
  }

  // Mirrors `pn_meta` in snippets/property-name-parts.liquid — same prefixes, same order.
  // `byravn` is ours (the sentinel / privatized preview): without it a stripped line would
  // read back as personalized on the next sweep and the gate would invert. `zepto` is the
  // separate `gate_probe` arm the Liquid gate applies right after pn_meta; the two are
  // folded together here because this function only ever feeds the gate.
  function isMetaKey(normalizedKey) {
    const meta = normalizedKey.toLowerCase();
    return (
      meta.slice(0, 14) === 'engraving_font' ||
      meta.slice(0, 4) === 'pplr' ||
      meta.slice(0, 3) === 'tpo' ||
      meta.slice(0, 5) === 'zepto' ||
      meta.slice(0, 6) === 'byravn' ||
      meta === 'gavepose_for'
    );
  }

  // A line break pasted into a Zepto field label survives form-encoding as `%0D%0A` inside
  // the stored property NAME; a price suffix like " (+60 kr.)" is baked into the app config
  // and is not part of the label's identity.
  function gateLabel(normalizedKey) {
    return normalizedKey
      .replace(/%0D/gi, '')
      .replace(/%0A/gi, '')
      .replace(/[\r\n]/g, '')
      .trim()
      .split(' (+')[0]
      .trim();
  }

  // Liquid `== blank` is true for '' and for whitespace-only values.
  function isBlankValue(value) {
    return value == null || String(value).trim() === '';
  }

  function specOnlyLabels() {
    const config = window.byRavnPreviewStrip;
    const list = config && config.specOnlyLabels;
    return Array.isArray(list) && list.length ? list : null;
  }

  // The checkout mirrors are submitted under the SHOPPER-LANGUAGE label (see
  // custom-symbol-keyboard.liquid), so the Danish blocklist can never match them by
  // label. Their names are recorded per line in `_byRavn_checkout_mirrors`; anything
  // in that list is a duplicate of a `_` original also present on the line, so the
  // gate must ignore it — same lookup rule cart-line-properties.liquid applies.
  //
  // `shifted` = Zepto's hide-in-checkout option has prefixed one MORE `_` to every key on
  // this line, so the mirror `X` now arrives as `_X`. That must be decided per LINE, not
  // per name: in the normal state `_X` is the RAW Danish field, and stripping a `_` off it
  // yields the mirror's name, so matching both forms unconditionally suppressed the
  // original too (fatal on Danish, where mirror name == Danish label). The manifest's own
  // key reveals the state, since Zepto shifted it as well. Its VALUE is never shifted.
  function mirrorManifest(props) {
    const direct = props['_byRavn_checkout_mirrors'];
    const raw = isBlankValue(direct) ? props['__byRavn_checkout_mirrors'] : direct;
    if (isBlankValue(raw)) return null;
    return { names: String(raw).split('|'), shifted: isBlankValue(direct) };
  }

  // true  → the line carries real personalization → NEVER strip.
  // false → nothing to preview → eligible.
  function lineHasPersonalization(item) {
    const blocklist = specOnlyLabels();
    if (!blocklist) return true;
    const props = item && item.properties;
    if (!props || typeof props !== 'object') return true;
    const manifest = mirrorManifest(props);
    const keys = Object.keys(props);
    for (let i = 0; i < keys.length; i++) {
      const value = props[keys[i]];
      if (isBlankValue(value)) continue;
      if (String(value).indexOf('/uploads/') !== -1) continue;
      if (manifest) {
        if (manifest.shifted) {
          const unshifted = keys[i].charAt(0) === '_' ? keys[i].slice(1) : null;
          if (unshifted && manifest.names.indexOf(unshifted) !== -1) continue;
        } else if (manifest.names.indexOf(keys[i]) !== -1) {
          continue;
        }
      }
      const key = normalizeKey(keys[i]);
      if (isMetaKey(key)) continue;
      // Unknown label ⇒ personalized. See zepto-field-registry.liquid for why.
      if (blocklist.indexOf(gateLabel(key)) === -1) return true;
    }
    return false;
  }

  // --- which property is the preview --------------------------------------------------

  // The VALUE test is authoritative. The problem is exclusively about PUBLIC properties —
  // `_pplr*` is plausibly how Zepto resolves artwork at fulfilment, so a `_` key is never
  // touched. The label is unstable (Zepto already ships two names for this), whereas
  // `/uploads/` is what the rest of the theme already trusts as the preview signal. The
  // name arm only fires on a blank or http(s) value, so real engraving text is unreachable
  // even on a field a merchant happens to call "Preview".
  function isPreviewProperty(rawKey, value) {
    if (String(rawKey).charAt(0) === '_') return false;
    const text = value == null ? '' : String(value);
    if (text.indexOf('/uploads/') !== -1) return true;
    if (PREVIEW_NAMES.indexOf(normalizeKey(rawKey).toLowerCase()) === -1) return false;
    return text.trim() === '' || /^https?:\/\//i.test(text);
  }

  function isOurKey(rawKey) {
    return rawKey === PRIVATE_KEY || rawKey === PURGE_SENTINEL;
  }

  function hasStrippableProperty(item) {
    const props = (item && item.properties) || {};
    return Object.keys(props).some((key) => isPreviewProperty(key, props[key]));
  }

  // --- identity that survives a re-key ------------------------------------------------

  // A line key is variantId:propertyHash, so it changes the moment we write properties and
  // cannot anchor the attempt counter. Variant + the sorted properties we do NOT touch is
  // identical before and after a strip, which is what lets the counter recognise a Zepto
  // re-attach. Two genuinely identical lines collide — harmless: they are stripped in the
  // same pass, and after the strip Shopify merges them anyway.
  function lineIdentity(item) {
    const props = item.properties || {};
    const pairs = Object.keys(props)
      .filter((key) => !isPreviewProperty(key, props[key]) && !isOurKey(key))
      .sort()
      .map((key) => `${key}=${String(props[key])}`);
    return `${item.id}|${pairs.join(UNIT)}`;
  }

  // --- attempt counters ---------------------------------------------------------------

  // sessionStorage, not a module variable: it survives navigation, so a Zepto re-attach
  // loop cannot restart itself once per page view.
  function readAttempts() {
    try {
      return JSON.parse(sessionStorage.getItem(STORE_KEY)) || {};
    } catch (error) {
      return {};
    }
  }

  function writeAttempts(attempts) {
    try {
      const keys = Object.keys(attempts);
      if (keys.length > STORE_LIMIT) {
        keys.slice(0, keys.length - STORE_LIMIT).forEach((key) => delete attempts[key]);
      }
      sessionStorage.setItem(STORE_KEY, JSON.stringify(attempts));
    } catch (error) {
      /* Safari private mode — the structural stop in sweep() is what actually ends a loop. */
    }
  }

  // --- the write -----------------------------------------------------------------------

  function strippedProperties(item) {
    const props = item.properties || {};
    const next = {};
    let previewUrl = '';
    Object.keys(props).forEach((key) => {
      if (isPreviewProperty(key, props[key])) {
        if (!previewUrl && String(props[key]).indexOf('/uploads/') !== -1) previewUrl = String(props[key]);
        return;
      }
      if (isOurKey(key)) return;
      next[key] = props[key];
    });
    if (MODE === 'privatize') {
      next[PRIVATE_KEY] = previewUrl || '1';
    } else if (!Object.keys(next).length) {
      next[PURGE_SENTINEL] = '1';
    }
    return next;
  }

  // No bundled sections here: a gavepose repair can follow the last strip, so sections
  // requested inline would paint a pre-repair cart. Sweep fetches them once at the end.
  // change.js RESETS an omitted `quantity` to 1, so the line's own quantity is resent
  // verbatim; when a strip makes two lines identical Shopify merges them and SUMS the
  // quantities, so the item_count invariant below still holds.
  function changeLine(key, quantity, properties) {
    return fetch(window.routes.cart_change_url, {
      ...window.fetchConfig(),
      body: JSON.stringify({ id: key, quantity, properties }),
    }).then((response) => response.json());
  }

  // Nothing here has to repair a gift-bag link: an unarmed line's bag is a nested child
  // whose parent_relationship Shopify migrates on a re-key, and an armed line's flat
  // `Gavepose: Ja` flag rides along in the property set the strip resends. (The ancient
  // `_gavepose_for` back-reference did need repair, and would have been orphaned by
  // every strip.)
  async function stripAll(candidates, cart) {
    const expectedItemCount = cart.item_count;
    const limit = Math.min(candidates.length, MAX_LINES_PER_SWEEP);
    let live = cart;
    let changed = false;

    for (let i = 0; i < limit; i++) {
      // Re-resolve against the cart the PREVIOUS call returned: every strip re-keys a line
      // and can reorder the cart, so the key captured at sweep start is only valid once.
      const identity = lineIdentity(candidates[i]);
      const item = live.items.find((candidate) => lineIdentity(candidate) === identity);
      if (!item) continue;
      // Re-verify immediately before writing, never on the sweep-start snapshot.
      if (!hasStrippableProperty(item) || lineHasPersonalization(item)) continue;

      const state = await changeLine(item.key, item.quantity, strippedProperties(item));
      if (state && (state.status || state.errors)) throw new Error(state.description || 'cart error');

      // Two spec-only lines are distinct today ONLY because Zepto gave each a different
      // preview hash; stripping both makes them identical and Shopify collapses them. A
      // merge with the total preserved is fine — a LOST unit is a silent bug, so stop hard.
      if (state.item_count !== expectedItemCount) {
        disabled = true;
        console.error('[byRavn-preview-strip] item_count changed', expectedItemCount, state.item_count);
        throw new Error('item_count invariant broken');
      }

      changed = true;
      live = state;
    }
    return changed;
  }

  // --- the sweep -----------------------------------------------------------------------

  async function sweep() {
    if (disabled || busy) return;
    const now = Date.now();
    if (now - lastSweepAt < MIN_SWEEP_INTERVAL_MS) {
      clearTimeout(trailingTimer);
      trailingTimer = setTimeout(sweep, MIN_SWEEP_INTERVAL_MS - (now - lastSweepAt));
      return;
    }

    let cart;
    try {
      cart = await fetch(`${window.routes.cart_url}.js`).then((response) => response.json());
    } catch (error) {
      lastSweepAt = Date.now(); // never write blind
      return;
    }
    if (!cart || !Array.isArray(cart.items)) {
      lastSweepAt = Date.now();
      return;
    }

    let candidates = cart.items.filter((item) => hasStrippableProperty(item) && !lineHasPersonalization(item));
    // The terminating fast path: nothing to do → no request, no repaint, no publish, so the
    // sweep our own cartUpdate re-triggers dies here. This, not the counter, is what ends
    // the loop in the normal case.
    if (!candidates.length) {
      lastSweepAt = Date.now();
      return;
    }

    // Bound a Zepto re-attach cycle. Count before the first request, so a crash mid-sweep
    // still counts.
    const attempts = readAttempts();
    candidates = candidates.filter((item) => (attempts[lineIdentity(item)] || 0) < MAX_ATTEMPTS);
    if (!candidates.length) {
      lastSweepAt = Date.now();
      return;
    }
    candidates.forEach((item) => {
      const identity = lineIdentity(item);
      attempts[identity] = (attempts[identity] || 0) + 1;
    });
    writeAttempts(attempts);

    busy = true;
    window.byRavnCart.setBusy(true);
    try {
      const changed = await stripAll(candidates, cart);
      // No cart surface on this page (cart_type 'page', and we are on a PDP) — nothing to
      // repaint, and the next cart render is server-side and already correct.
      const surface = window.byRavnCart.surfaces()[0];
      if (changed && surface) {
        window.byRavnCart.paintAllSurfaces(await window.byRavnCart.fetchCartSections(), surface, PUBLISH_SOURCE);
      }
    } catch (error) {
      console.error('[byRavn-preview-strip]', error);
      // A failed write leaves the rendered keys stale; a fresh server render heals them.
      window.byRavnCart.surfaces().forEach((surface) => surface.onCartUpdate());
    } finally {
      busy = false;
      window.byRavnCart.setBusy(false);
      lastSweepAt = Date.now();
    }
  }

  function scheduleSweep(delay) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(sweep, delay == null ? SWEEP_DEBOUNCE_MS : delay);
  }

  // --- triggers -------------------------------------------------------------------------

  function boot() {
    // cart-line-properties.liquid already emits .cart-line--unpersonalized server-side, and
    // the drawer renders on every page when cart_type is 'drawer' — so this costs no extra
    // Liquid and, on the overwhelming majority of page views, not even a /cart.js request.
    // It is a hint, not proof, so the sweep still fetches and decides for itself.
    if (document.querySelector('.cart-line--unpersonalized')) scheduleSweep(0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Every theme-driven mutation and re-render: Dawn's updateQuantity, the gavepose toggle,
  // cart recommendations. Our own repaint publishes too — skip it rather than pay for the
  // /cart.js round trip that would only hit the fast path above.
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event && event.source === PUBLISH_SOURCE) return;
    scheduleSweep();
  });

  // Zepto's add. byRavn-cart-drawer-open.js owns the XMLHttpRequest patch and broadcasts
  // this; Zepto attaches the preview out-of-band AFTER that add returns, so wait, then
  // allow exactly one retry in case it is slower than usual. One retry, then stop — no
  // polling.
  document.addEventListener('byRavn:zepto-add', () => {
    setTimeout(() => {
      sweep();
      setTimeout(() => {
        if (!disabled) sweep();
      }, ZEPTO_ADD_RETRY_MS);
    }, ZEPTO_ADD_DELAY_MS);
  });
})();
